// Chat-message persistence helpers for the chatboard React app.
// Server-side migration + routes shipped in Phase B; full design at
// sooth-cloud-design/docs/chatboard/2026-05-13-chat-message-persistence-design.md.
//
// Pure functions taking deps via opts so they're easy to test in isolation;
// App.jsx wraps these with setMsgs/setActive callbacks where the actual UI
// state lives.
//
// Both helpers are best-effort: persistence is fire-and-forget, load
// failures fall back to an empty thread so the chat UX never blocks on
// the DB.

/**
 * POST /api/forecasts/:id/messages — fire-and-forget message persist.
 *
 * No-op when forecastId is falsy (the user submitted before the forecast
 * row was allocated; the message is preview-only and gets lost on reload).
 * Phase B's INSERT-before-upstream flow means a forecastId is available
 * from submit-time onward, so this only triggers for the very-early
 * pre-submit case if any.
 *
 * Swallows fetch errors and non-2xx responses — persistence failure must
 * never crash the chat UI.
 */
export async function persistMessage(forecastId, msg, opts = {}) {
  if (!forecastId) return;
  const fetchImpl = opts.fetchImpl || fetch;
  try {
    await fetchImpl(`/api/forecasts/${encodeURIComponent(forecastId)}/messages`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        role: msg.role,
        content: msg.content,
        metadata: msg.metadata || {},
      }),
    });
  } catch {
    // best-effort; chat continues
  }
}

/**
 * GET /api/forecasts/:id/messages — load the persisted thread for a
 * forecast on forecast-load (saved-list click or reload-resume).
 *
 * Returns the messages array on 200, or [] on 404 / network failure /
 * falsy forecastId. App.jsx maps the rows to the UI shape
 * ({role, content}) before seeding setMsgs.
 */
export async function loadMessages(forecastId, opts = {}) {
  if (!forecastId) return [];
  const fetchImpl = opts.fetchImpl || fetch;
  try {
    const r = await fetchImpl(`/api/forecasts/${encodeURIComponent(forecastId)}/messages`, {
      method: "GET",
      credentials: "include",
    });
    if (!r.ok) return [];
    const body = await r.json();
    return Array.isArray(body.messages) ? body.messages : [];
  } catch {
    return [];
  }
}
